// @flow
import * as React from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { sprintf } from 'sprintf-js';
import styled, { css } from 'styled-components';
import type { CurrentUser } from 'api/currentUser';
import type { Notification } from 'api/userNotifications';
import { trackClicked } from 'utils/analytics';
import { promoteResumeDatabase, hasResumeDb } from 'utils/resumeDatabase';
import {
  notificationIsDismissed,
  dismissNotification as dismissLocalNotification,
  NOTIFICATIONS
} from 'utils/localNotifications';
import { isDefined } from 'utils/functional/type';
import ResumeDatabaseResponseContainer from 'containers/ResumeDatabaseResponseContainer';
import Box from 'components/Box';
import Divider from 'components/Divider';
import Meter from 'components/Meter';
import PostListingLink from 'components/PostListingLink';
import { Link as RouterLink } from 'components/RouterLink';
import Text from 'components/Text';
import UserNotification from 'components/UserNotification';
import Notifications from 'components/Notifications';
import { PrimaryButtonRouterLink, LinkButton } from 'components/Button';
import DropdownMenu, {
  DropdownMenuItem,
  DropdownMenuItemRouter,
  DropdownMenuSection,
  DropdownMenuSectionItemRouter,
  DropdownMenuSectionTitle
} from 'components/PageHeaderDropdownMenu';
import { hasUsedAts } from 'utils/ats';
import {
  fetchNotifications,
  dismissNotifications
} from '../redux/main/actions/userNotifications';
import { colors, fontWeights, transition } from '../theme';

const { useState, useEffect } = React;

const NAV_WIDTH = {
  en: 900,
  es: 1050,
  pt: 1000
};

const getCount = (notifications: Array<Notification>) =>
  notifications.reduce(
    (acc, n) =>
      n.type === 'NEW_APPLICATIONS_BY_LISTING' ? acc + n.data.count : acc + 1,
    0
  );

const linkStyles = css`
  text-decoration: underline;
  white-space: nowrap;
  font-weight: ${fontWeights.semiBold};
`;

const StyledRouterLink = styled(RouterLink)`
  ${linkStyles}
`;

const StyledPostListingLink = styled(PostListingLink)`
  ${linkStyles}
`;

const StyledLinkButton = styled(LinkButton)`
  color: ${colors.battleshipGrey};
  font-size: inherit;
  font-weight: ${fontWeights.normal};
  text-decoration: underline;
`;

const ConditionalDropdownMenuItem = ({
  to,
  isStaff,
  ...props
}: {
  to: string,
  isStaff?: boolean
}) => {
  const Component = isStaff ? DropdownMenuItem : DropdownMenuItemRouter;
  const linkProps = isStaff ? { href: to } : { to };
  return <Component {...props} {...linkProps} />;
};

const Badge = ({ count, ...props }: { count: ?number }) => (
  <Box
    display="flex"
    justifyContent="center"
    alignItems="center"
    width="calc(1em + 4px)"
    height="calc(1em + 4px)"
    flex="0 0 auto"
    borderRadius="50%"
    color="white"
    fontWeight={isDefined(count) ? fontWeights.normal : fontWeights.bold}
    lineHeight={1.375}
    {...props}
  >
    <Box fontSize={`calc(1em - ${!count ? 1 : count > 9 ? 2 : 0}px)`}>
      {!isDefined(count) ? '!' : Math.min(count, 99)}
      {count && count > 99 && '+'}
    </Box>
  </Box>
);

const ProfileSection = ({
  closeMenu,
  currentUser
}: {
  closeMenu: Function,
  currentUser: CurrentUser
}) => (
  <>
    <Divider light my={0} />
    <Box minWidth={200} px={12} pt={16} pb={24} textAlign="center">
      <Box mb="8px" color={colors.battleshipGrey}>
        {getText('Your profile is incomplete')}
      </Box>
      <Meter
        current={1}
        total={3}
        color="turtleGreen"
        delay={0}
        duration={0.5}
      />
      <Box mt={20} mb="8px">
        <PrimaryButtonRouterLink
          to="/dashboard/employment-profile/get-discovered"
          size="small"
          square
          width="100%"
          onClick={() => {
            closeMenu();
            trackClicked('Add Resume', {
              context: 'Header User Dropdown'
            });
          }}
        >
          {currentUser.resumeId
            ? getText('Share Resume')
            : getText('Add Resume')}
        </PrimaryButtonRouterLink>
      </Box>
      <Box width={1} display="flex" flexDirection="column" alignItems="center">
        <ResumeDatabaseResponseContainer>
          {({ dismiss }) => (
            <StyledLinkButton
              onClick={() => {
                dismiss();
                closeMenu();
                trackClicked('Dismiss Resume Database CTA', {
                  context: 'Header User Dropdown'
                });
              }}
            >
              {getText('No, thanks')}
            </StyledLinkButton>
          )}
        </ResumeDatabaseResponseContainer>
      </Box>
    </Box>
  </>
);

const AtsCtaNotification = ({ dismiss }: { dismiss: Function }) => (
  <Box p={12} minWidth={260} borderTop={`1px solid ${colors.aluminum}`}>
    <UserNotification status="NEW" dismiss={dismiss}>
      <Text
        dangerouslySetInnerHTML={{
          __html: sprintf(
            getText(
              `One more reason to post on Idealist:
              Try%(nonBreakingSpace)sour free Applicant Tracker%(nonBreakingSpace)stoday!`
            ),
            { nonBreakingSpace: '&nbsp;' }
          )
        }}
      />

      <Box display="flex" mt={16}>
        <StyledRouterLink to={getText('/applicant-tracker')} onClick={dismiss}>
          {getText('Learn More')}
        </StyledRouterLink>
        <Box mx="8px" width="1px" bg={colors.aluminum} />
        <StyledPostListingLink
          data-qa-id="post-a-listing-ats-dropdown"
          onClick={() => {
            trackClicked('Post A Listing', {
              context: 'User Dropdown ATS CTA'
            });
            dismiss();
          }}
        >
          {getText('Post a Listing')}
        </StyledPostListingLink>
      </Box>
    </UserNotification>
  </Box>
);

const PageHeaderAccountDropdown = ({
  currentUser,
  isStaff
}: {
  currentUser: CurrentUser,
  isStaff: boolean
}) => {
  const dispatch = useDispatch();
  const { userNotifications } = useSelector(state => ({
    userNotifications: state.userNotifications
  }));

  const [atsNotificationDismissed, setAtsNotificationDismissed] = useState(
    notificationIsDismissed(NOTIFICATIONS.ORG_ADMIN_USER_DROPDOWN_ATS_CTA)
  );
  const promoteResumeDb = promoteResumeDatabase({ currentUser });
  const promoteAtsToOrg = !atsNotificationDismissed && !hasUsedAts();
  const showBadge =
    promoteResumeDb ||
    promoteAtsToOrg ||
    (userNotifications.notifications &&
      userNotifications.notifications.length > 0);

  const dismissAtsNotification = () => {
    dismissLocalNotification(NOTIFICATIONS.ORG_ADMIN_USER_DROPDOWN_ATS_CTA);
    setAtsNotificationDismissed(true);
  };

  const dismissNotification = ids => {
    dispatch(dismissNotifications(ids));
  };

  useEffect(
    () => {
      if (currentUser.hasNotifications && !userNotifications.isLoaded) {
        dispatch(fetchNotifications());
      }
    },
    [currentUser.hasNotifications, dispatch, userNotifications.isLoaded]
  );

  return (
    <Box position="relative">
      <DropdownMenu
        aria-label={getText('Account Information')}
        title={() => (
          <Box
            display={['none', null, null, 'flex']}
            alignItems="center"
            maxWidth={[
              null,
              null,
              null,
              `calc(100vw - ${NAV_WIDTH[USER_LOCALE]}px)`
            ]}
            minWidth={[null, null, null, 32]}
          >
            <Box
              whiteSpace="noWrap"
              textOverflow="ellipsis"
              overflow="hidden"
              pl={showBadge && 'calc(1em + 10px)'}
            >
              {/* TRANSLATORS: user greeting at top of page */ sprintf(
                getText('Hi, %(name)s!'),
                {
                  name: currentUser.firstName || currentUser.email
                }
              )}
            </Box>
          </Box>
        )}
        data-qa-id="nav-dropdown"
      >
        {({ closeMenu, open }) => (
          <>
            {promoteResumeDb && (
              <ProfileSection closeMenu={closeMenu} currentUser={currentUser} />
            )}

            {userNotifications.isLoaded && userNotifications.notifications && (
              <Notifications
                notifications={userNotifications.notifications}
                dismiss={dismissNotification}
                open={open}
              />
            )}

            {promoteAtsToOrg && (
              <AtsCtaNotification dismiss={dismissAtsNotification} />
            )}

            {hasResumeDb(currentUser) && (
              <ConditionalDropdownMenuItem
                to="/dashboard/employment-profile"
                isStaff={isStaff}
                onClick={closeMenu}
              >
                {getText('Employment Profile')}
              </ConditionalDropdownMenuItem>
            )}

            <ConditionalDropdownMenuItem
              to="/dashboard/account-settings"
              isStaff={isStaff}
              onClick={closeMenu}
            >
              {getText('My Account')}
            </ConditionalDropdownMenuItem>

            {currentUser.orgs.length > 0 && (
              <DropdownMenuSection>
                <DropdownMenuSectionTitle>
                  {getText('My Organizations')}
                </DropdownMenuSectionTitle>
                {currentUser.orgs.map(org =>
                  isStaff ? (
                    <DropdownMenuItem
                      key={org.id}
                      href={
                        org.hasGradProgram
                          ? `/admin/${org.id}/grad-program`
                          : `/admin/${org.id}`
                      }
                      onClick={closeMenu}
                    >
                      {org.name}
                    </DropdownMenuItem>
                  ) : (
                    <DropdownMenuSectionItemRouter
                      key={org.id}
                      to={
                        org.hasGradProgram
                          ? `/admin/${org.id}/grad-program`
                          : `/admin/${org.id}`
                      }
                      onClick={closeMenu}
                    >
                      {org.name}
                    </DropdownMenuSectionItemRouter>
                  )
                )}
              </DropdownMenuSection>
            )}

            {currentUser.groups.length > 0 && (
              <DropdownMenuSection>
                <DropdownMenuSectionTitle>
                  {getText('My Groups')}
                </DropdownMenuSectionTitle>
                {currentUser.groups.map(group => (
                  <DropdownMenuSectionItemRouter
                    key={group.id}
                    to={`/group-admin/${group.id}`}
                    onClick={closeMenu}
                  >
                    {group.name}
                  </DropdownMenuSectionItemRouter>
                ))}
              </DropdownMenuSection>
            )}

            <DropdownMenuItem href="/logout" data-qa-id="logout-link">
              {getText('Log Out')}
            </DropdownMenuItem>
          </>
        )}
      </DropdownMenu>

      {showBadge && (
        <Box
          position="absolute"
          zIndex={2000}
          left={12}
          top={11}
          pointerEvents="none"
        >
          <Badge
            data-qa-id={
              userNotifications.notifications &&
              userNotifications.notifications.length
                ? 'notification-count'
                : 'notification-icon'
            }
            bg={
              userNotifications.notifications &&
              userNotifications.notifications.length
                ? colors.poppy
                : colors.waterBlue
            }
            transition={`all ${transition}`}
            count={
              userNotifications.notifications &&
              userNotifications.notifications.length
                ? getCount(userNotifications.notifications)
                : null
            }
          />
        </Box>
      )}
    </Box>
  );
};

export default PageHeaderAccountDropdown;
